import { useCallback, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import intl from "react-intl-universal";
import BR from "country-flag-icons/react/1x1/BR";
import US from "country-flag-icons/react/1x1/US";
import ES from "country-flag-icons/react/1x1/ES";
import AE from "country-flag-icons/react/1x1/AE";
import { UserContext } from "../../../Contexts/User";
import {
  PORTAL_DROPDOWN_BACKDROP_Z_INDEX,
  PORTAL_DROPDOWN_Z_INDEX,
  usePortalDropdownPosition,
} from "../../../Hooks/usePortalDropdownPosition";
import { supportedLocales } from "../../../Utils/i18n";

const FLAG_SIZE = 14;
const MENU_MAX_HEIGHT = 220;

type FlagComponent = React.ComponentType<{
  title?: string;
  style?: React.CSSProperties;
}>;

const LANGUAGE_OPTIONS: Record<string, { label: string; shortLabel: string; Flag: FlagComponent }> = {
  "pt-BR": { label: "Português (BR)", shortLabel: "PT", Flag: BR },
  "en-US": { label: "English", shortLabel: "EN", Flag: US },
  "es-ES": { label: "Español", shortLabel: "ES", Flag: ES },
  "ar-AE": { label: "العربية (دبي)", shortLabel: "AR", Flag: AE },
};

type LanguageFlagProps = {
  Flag: FlagComponent;
  label: string;
};

const LanguageFlag = ({ Flag, label }: LanguageFlagProps) => (
  <span className="language-selector__flag" aria-hidden="true">
    <Flag title={label} style={{ width: FLAG_SIZE, height: FLAG_SIZE, display: "block" }} />
  </span>
);

type LanguageSelectorProps = {
  className?: string;
  id?: string;
  variant?: "dropdown" | "segmented";
};

export const LanguageSelector = ({
  className = "",
  id = "language-select",
  variant = "dropdown",
}: LanguageSelectorProps) => {
  const [stateUserContext, dispatchUserContext] = useContext(UserContext);
  const [isOpen, setIsOpen] = useState(false);
  const { triggerRef, menuRef, menuStyle } = usePortalDropdownPosition(isOpen, MENU_MAX_HEIGHT);
  const listboxId = `${id}-listbox`;

  const currentLocale = stateUserContext.userLanguage;
  const currentOption = LANGUAGE_OPTIONS[currentLocale] ?? LANGUAGE_OPTIONS["pt-BR"];

  const selectLanguage = useCallback(
    (nextLanguage: string) => {
      dispatchUserContext({ type: "setUserLanguage", userLanguage: nextLanguage });
      setIsOpen(false);
    },
    [dispatchUserContext]
  );

  const closeMenu = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  if (variant === "segmented") {
    return (
      <div
        className={`language-segments extract-view-tabs ${className}`.trim()}
        role="radiogroup"
        aria-labelledby={`${id}-label`}
      >
        <span className="sr-only" id={`${id}-label`}>
          {intl.get("words.language")}
        </span>
        {supportedLocales.map((locale) => {
          const option = LANGUAGE_OPTIONS[locale];
          if (!option) return null;

          const isSelected = locale === currentLocale;

          return (
            <button
              key={locale}
              type="button"
              id={isSelected ? id : undefined}
              role="radio"
              aria-checked={isSelected}
              aria-label={option.label}
              className={`language-segment extract-view-tab ${
                isSelected ? "extract-view-tab--active" : ""
              }`}
              onClick={() => selectLanguage(locale)}
            >
              <LanguageFlag Flag={option.Flag} label={option.label} />
              <span>{option.shortLabel}</span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className={`language-selector${isOpen ? " language-selector--open" : ""} ${className}`.trim()}
    >
      <span className="language-selector__label" id={`${id}-label`}>
        {intl.get("words.language")}
      </span>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        className="language-selector__trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-labelledby={`${id}-label ${id}`}
        onClick={() => setIsOpen((open) => !open)}
      >
        <LanguageFlag Flag={currentOption.Flag} label={currentOption.label} />
        <span className="language-selector__value">{currentOption.label}</span>
        <span className="language-selector__chevron" aria-hidden="true" />
      </button>
      {isOpen && menuStyle
        ? createPortal(
            <>
              <div
                className="fixed inset-0"
                style={{ zIndex: PORTAL_DROPDOWN_BACKDROP_Z_INDEX }}
                onClick={closeMenu}
                aria-hidden="true"
              />
              <div
                ref={menuRef}
                className={className.trim()}
                style={{
                  ...menuStyle,
                  zIndex: PORTAL_DROPDOWN_Z_INDEX,
                  overflow: "auto",
                }}
              >
                <ul
                  id={listboxId}
                  className="language-selector__menu"
                  role="listbox"
                  aria-labelledby={`${id}-label`}
                >
                  {supportedLocales.map((locale) => {
                    const option = LANGUAGE_OPTIONS[locale];
                    if (!option) return null;

                    const isSelected = locale === currentLocale;

                    return (
                      <li key={locale} role="none">
                        <button
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          className={`language-selector__option${
                            isSelected ? " language-selector__option--selected" : ""
                          }`}
                          onClick={() => selectLanguage(locale)}
                        >
                          <LanguageFlag Flag={option.Flag} label={option.label} />
                          <span>{option.label}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </>,
            document.body
          )
        : null}
    </div>
  );
};
